import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { sanitizeJoinCodeInput } from '../../accounts/helpers/b2bAccount.helper';
import {
  PENDING_B2B_JOIN_SESSION_KEY,
  PENDING_B2B_JOIN_STORAGE_KEY,
  PENDING_B2B_JOIN_TTL_MS,
} from '../constants/pendingB2bJoin.constants';

interface PendingB2bJoinPayload {
  joinCode: string;
  savedAt: number;
}

function isValidJoinCode(code: string): boolean {
  const normalized = sanitizeJoinCodeInput(code);
  return normalized.length >= 4 && normalized.length <= 32;
}

function parsePayload(raw: string | null): PendingB2bJoinPayload | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (
      parsed &&
      typeof parsed === 'object' &&
      'joinCode' in parsed &&
      'savedAt' in parsed &&
      typeof (parsed as PendingB2bJoinPayload).joinCode === 'string' &&
      typeof (parsed as PendingB2bJoinPayload).savedAt === 'number'
    ) {
      const payload = parsed as PendingB2bJoinPayload;
      if (!isValidJoinCode(payload.joinCode)) return null;
      if (Date.now() - payload.savedAt > PENDING_B2B_JOIN_TTL_MS) return null;
      return { joinCode: sanitizeJoinCodeInput(payload.joinCode), savedAt: payload.savedAt };
    }
  } catch {
    return null;
  }
  return null;
}

/**
 * `/join?join=CODE` only — used to persist join intent from returnUrl before OAuth.
 */
export function extractJoinCodeFromPublicJoinPath(path: string): string | null {
  const trimmed = path.trim();
  if (!trimmed.startsWith('/join')) return null;
  const q = trimmed.indexOf('?');
  if (q === -1) return null;
  if (trimmed.slice(0, q) !== '/join') return null;
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(trimmed.slice(q + 1));
  } catch {
    return null;
  }
  const keys = [...params.keys()];
  if (keys.length !== 1 || keys[0] !== 'join') return null;
  const raw = params.get('join');
  if (!raw || !isValidJoinCode(raw)) return null;
  return sanitizeJoinCodeInput(raw);
}

export async function savePendingB2bJoinCode(joinCodeRaw: string): Promise<void> {
  if (!isValidJoinCode(joinCodeRaw)) return;
  const payload: PendingB2bJoinPayload = {
    joinCode: sanitizeJoinCodeInput(joinCodeRaw),
    savedAt: Date.now(),
  };
  const serialized = JSON.stringify(payload);
  await AsyncStorage.setItem(PENDING_B2B_JOIN_STORAGE_KEY, serialized);
  if (Platform.OS === 'web' && typeof sessionStorage !== 'undefined') {
    try {
      sessionStorage.setItem(PENDING_B2B_JOIN_SESSION_KEY, serialized);
    } catch {
      // ignore quota / privacy mode
    }
  }
}

export async function savePendingB2bJoinFromReturnPath(returnPath: string): Promise<void> {
  const code = extractJoinCodeFromPublicJoinPath(returnPath);
  if (!code) return;
  await savePendingB2bJoinCode(code);
}

export async function getPendingB2bJoinCode(): Promise<string | null> {
  let payload = parsePayload(await AsyncStorage.getItem(PENDING_B2B_JOIN_STORAGE_KEY));
  if (!payload && Platform.OS === 'web' && typeof sessionStorage !== 'undefined') {
    try {
      payload = parsePayload(sessionStorage.getItem(PENDING_B2B_JOIN_SESSION_KEY));
      if (payload) {
        await AsyncStorage.setItem(PENDING_B2B_JOIN_STORAGE_KEY, JSON.stringify(payload));
      }
    } catch {
      // ignore
    }
  }
  return payload?.joinCode ?? null;
}

export async function clearPendingB2bJoinCode(): Promise<void> {
  await AsyncStorage.removeItem(PENDING_B2B_JOIN_STORAGE_KEY);
  if (Platform.OS === 'web' && typeof sessionStorage !== 'undefined') {
    try {
      sessionStorage.removeItem(PENDING_B2B_JOIN_SESSION_KEY);
    } catch {
      // ignore
    }
  }
}
